/**
 * Session-scoped TTL cache for near-static lookup payloads.
 *
 * PERF FIX: on every cold page load the header (src/layouts/dashboard/header/
 * index.jsx) and the dashboard layout (src/layouts/dashboard/index.jsx) refetch
 * a handful of near-static lookups every time the practice mounts/changes,
 * even though apiCall.js (~38) only dedupes requests that are still IN-FLIGHT —
 * there is no time-based cache once a request has settled. This helper adds
 * one, backed by sessionStorage so it survives route changes/remounts within a
 * tab but never leaks across tabs, devices, or logins.
 *
 * Applied to exactly:
 *   - fetchMergeTagKeys()          → /api/v1/utils/mergetags/            (global)
 *   - getGlobalTablePreferences()  → /api/v1/utils/globaltablecolumnpreference/ (global)
 *   - fetchMergeTagValueForPractice() → /api/v1/practices/{id}/get_context/    (practice-scoped)
 *   - fetchMergeTagValueForUser()     → /api/v1/auth/user/get_context/        (user-scoped)
 *
 * Deliberately NOT applied to getSchemaList() (/utils/enums/) or
 * getChoicesList() (/practices/{id}/choices/) — the header already caches
 * those results in localStorage keyed by field name (~189-196), including the
 * staleness patch for the truncated ArchiveReasonsData cache (~173-184).
 * Duplicating that with a second cache layer would just add a second place a
 * similar staleness bug could hide.
 *
 * Versioning: every entry is stamped with a version. A version mismatch (or a
 * TTL expiry) is treated as a miss — no length-based heuristics, which is
 * exactly the bug class the ArchiveReasonsData patch had to work around after
 * the fact. `public/version.json` / the service worker's APP_VERSION are not
 * synchronously readable from application code (the build stamp is baked into
 * the service worker file and only reachable via an async postMessage
 * round-trip — see useVersionCheck.js), so callers either pass their own
 * version or fall back to this module's own bumpable constant.
 *
 * Storage never throws out of this module: sessionStorage can be unavailable
 * (private browsing, disabled storage, quota) and every call here degrades to
 * "treat it as a cache miss" / "silently drop the write" instead.
 */

const STORAGE_KEY_PREFIX = 'dental-sem.static-lookup-cache';

// Bump this whenever the shape of a cached payload changes, to invalidate
// every existing sessionStorage entry from an older deploy in one move.
const CACHE_HELPER_VERSION = 1;

export const STATIC_LOOKUP_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
export const STATIC_LOOKUP_CACHE_VERSION = CACHE_HELPER_VERSION;

function buildStorageKey(key) {
    return `${STORAGE_KEY_PREFIX}:${key}`;
}

function safeGetItem(storageKey) {
    try {
        return sessionStorage.getItem(storageKey);
    } catch (_e) {
        return null;
    }
}

function safeSetItem(storageKey, value) {
    try {
        sessionStorage.setItem(storageKey, value);
        return true;
    } catch (_e) {
        // sessionStorage unavailable, disabled, or over quota — the cache is
        // best-effort, so silently drop the write rather than surface an error.
        return false;
    }
}

function safeRemoveItem(storageKey) {
    try {
        sessionStorage.removeItem(storageKey);
    } catch (_e) {
        /* nothing to clean up if storage isn't reachable */
    }
}

/**
 * Reads a cached value for `key`, or `undefined` on a miss (absent, expired,
 * version-mismatched, corrupted, or storage unavailable).
 *
 * @param {string} key
 * @param {{ ttlMs: number, version?: number }} options
 */
export function getCached(key, { ttlMs, version = CACHE_HELPER_VERSION } = {}) {
    if (!key || !ttlMs) return undefined;

    const raw = safeGetItem(buildStorageKey(key));
    if (!raw) return undefined;

    let entry;
    try {
        entry = JSON.parse(raw);
    } catch (_e) {
        return undefined;
    }

    if (!entry || typeof entry !== 'object') return undefined;
    if (entry.v !== version) return undefined;
    if (typeof entry.at !== 'number' || Date.now() - entry.at > ttlMs) return undefined;

    return entry.data;
}

/**
 * Writes `value` under `key`, stamped with the current time and version.
 * Never throws; returns false if the write could not be persisted.
 *
 * @param {string} key
 * @param {*} value
 * @param {{ version?: number }} options
 */
export function setCached(key, value, { version = CACHE_HELPER_VERSION } = {}) {
    if (!key) return false;
    let serialized;
    try {
        serialized = JSON.stringify({ v: version, at: Date.now(), data: value });
    } catch (_e) {
        return false;
    }
    return safeSetItem(buildStorageKey(key), serialized);
}

/** Removes a single cached entry (no-op if absent or storage unavailable). */
export function clearCached(key) {
    if (!key) return;
    safeRemoveItem(buildStorageKey(key));
}

/**
 * Removes every entry this helper has ever written to sessionStorage.
 *
 * Called from two invalidation points:
 *   - practice switch (src/redux/practiceSwitchReset.js) — practice-scoped
 *     entries (e.g. get_context:practice:<id>) must not leak into the
 *     incoming practice's first render.
 *   - logout (performLocalLogout in src/contexts/JWTContext.jsx) — user-scoped
 *     entries (e.g. get_context:user:<id>) must not leak to the next account
 *     on a shared tab/browser.
 *
 * Global entries (mergetags, globaltablecolumnpreference) are also cleared on
 * both events — simpler and safer than trying to keep them alive, and they
 * refill on the very next mount within the same TTL window anyway.
 */
export function clearAllStaticLookupCache() {
    try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith(`${STORAGE_KEY_PREFIX}:`)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => safeRemoveItem(k));
    } catch (_e) {
        /* sessionStorage unreachable — nothing to clear */
    }
}
